import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { GET, POST, PUT } from "../../../api/requests";
import { SimpleModal } from "../../../components/Modals/SimpleModal";
import Button from "../../../components/Button";
import NavBanner from "../../../components/Banners/NavBanner";
import { useDialog } from "../../../contexts/DialogContext";

export default function ManageCredentials() {
  const { credentialId } = useParams();
  const navigate = useNavigate();
  const { openDialog } = useDialog();

  const [note, setNote] = useState("");
  const [tenant, setTenant] = useState("");
  const [authType, setAuthType] = useState("browser");
  const [credentialPayload, setCredentialPayload] = useState({});
  const [platform, setPlatform] = useState("");

  const { state } = useLocation();
  const [credential, setCredential] = useState(state?.credential);

  const {
    data: fetchedCredential,
    isLoading,
    error,
  } = useQuery(
    ["credential", credentialId],
    async () => {
      if (!credentialId) return undefined;
      const response = await GET(`/credentials/${credentialId}`);
      return response;
    },
    {
      enabled: !!credentialId,
      onSuccess: (data) => {
        if (data) {
          setCredential(data);
          setNote(data.note || "");
          setTenant(data.tenant || "");
          setAuthType(data.authType || "browser");
          setPlatform(data.platform || "");
          setCredentialPayload(data.credentialPayload || {});
        }
      },
    }
  );

  useEffect(() => {
    console.log("credentialId:", credentialId);
    console.log("isLoading:", isLoading);
    console.log("error:", error);
    console.log("fetchedCredential:", fetchedCredential);
  }, [credentialId, isLoading, error, fetchedCredential]);

  const { mutate: createCredentialMutation } = useMutation(
    (formData) => POST(`/credentials`, formData),
    {
      onSuccess: () => {
        navigate(-1);
      },
      onError: (error) => {
        openDialog(
          <SimpleModal
            titleText="There was an error"
            bodyText={`Received this from the server: ${
              error.message || "unknown error"
            }`}
            variant="error"
          />
        );
      },
    }
  );

  const { mutate: updateCredentialMutation } = useMutation(
    (formData) => PUT(`/credentials/${credentialId}`, formData),
    {
      onSuccess: () => {
        navigate(-1);
      },
      onError: (error) => {
        openDialog(
          <SimpleModal
            titleText="There was an error"
            bodyText={`Received this from the server: ${
              error.message || "unknown error"
            }`}
            variant="error"
          />
        );
      },
    }
  );

  const handleSave = async () => {
    const formData = {
      note,
      tenant,
      platform,
      authType,
      credentialPayload,
    };

    if (credentialId) {
      updateCredentialMutation(formData);
    } else {
      createCredentialMutation(formData);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (credential) {
      const { note, tenant, platform, authType, credentialPayload } =
        credential;
      setNote(note || "");
      setTenant(tenant || "");
      setPlatform(platform || "");
      setAuthType(authType || "");
      setCredentialPayload(credentialPayload || {});
    }
  }, [credential]);

  return (
    <>
      <NavBanner />
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-12 lg:px-12">
        <h1
          className={`text-2xl font-bold text-gray-900 ${
            credentialId ? "mb-2" : "mb-8"
          }`}
        >
          {credentialId ? "Edit Credential" : "Create Credential"}
        </h1>

        {credentialId && (
          <p className="text-gray-500 mb-8">
            Note: updating a credential will create a new credential in the
            secret manager, and overwrite the credentialName in the current
            supabase credential entry.
          </p>
        )}

        {/* Tenant */}
        <div>
          <label
            htmlFor="title"
            className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
          >
            Tenant
          </label>
          <div className="mt-1 mb-8">
            <input
              id="title"
              name="title"
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="Tenant name"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* source table and instance */}
        <div className="flex items-start space-x-4 mb-8">
          <div className="flex-1">
            <label
              htmlFor="table"
              className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
            >
              Authentication type
            </label>
            <div className="mt-1">
              <select
                value={authType}
                onChange={(e) => setAuthType(e.target.value)}
                id="table"
                name="table"
                autoComplete="source-table-name"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              >
                <option>browser</option>
                <option>api</option>
                <option>oauth</option>
                <option>webhook</option>
              </select>
            </div>
          </div>

          <div className="flex-1">
            <label
              htmlFor="table"
              className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
            >
              Platform
            </label>
            <div className="mt-1">
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                id="table"
                name="table"
                autoComplete="source-table-name"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              >
                <option>E2OPEN</option>
                <option>FLEXPORT</option>
                <option>PORTPRO</option>
                <option>MCLEOD</option>
                <option>AXLE</option>
                <option>CONVOY</option>
                <option>CROWN</option>
                <option>MOTHERSHIP</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          {authType === "browser" && (
            <>
              <div className="sm:col-span-3">
                <label
                  htmlFor="username"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Username
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="username"
                    id="username"
                    value={credentialPayload.username || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <div className="mt-2">
                  <input
                    type="password"
                    name="password"
                    id="password"
                    value={credentialPayload.password || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </>
          )}
          {authType === "oauth" && (
            <>
              <div className="sm:col-span-3">
                <label
                  htmlFor="clientId"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Client ID
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="clientId"
                    id="clientId"
                    value={credentialPayload.clientId || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        clientId: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="clientSecret"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Client secret
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="clientSecret"
                    id="clientSecret"
                    value={credentialPayload.clientSecret || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        clientSecret: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Audience
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="audience"
                    id="audience"
                    value={credentialPayload.audience || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        audience: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="grantType"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Grant type
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="grantType"
                    id="grantType"
                    value={credentialPayload.grantType || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        grantType: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div className="sm:col-span-6">
                <label
                  htmlFor="scope"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Scope
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="scope"
                    id="scope"
                    value={credentialPayload.scope || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        scope: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Refresh endpoint (optional)
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="refreshEndpoint"
                    id="refreshEndpoint"
                    value={credentialPayload.refreshEndpoint || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        refreshEndpoint: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Token extraction path
                </label>
                <p className="text-xs text-slate-700">
                  Dot notation path to extract the token from the response body
                </p>
                <div className="mt-2">
                  <input
                    type="text"
                    name="tokenPath"
                    id="tokenPath"
                    value={credentialPayload.tokenPath || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        tokenPath: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Refresh headers (optional)
                </label>
                <p className="text-xs text-slate-500">
                  Supports templating for access_token and refresh_token:{" "}
                  {JSON.stringify({
                    Authorization: "Bearer {{access_token}}",
                    "Content-Type": "multipart/form-data",
                  })}
                </p>
                <div className="mt-2">
                  <input
                    type="text"
                    name="refreshHeaders"
                    id="refreshHeaders"
                    value={credentialPayload.refreshHeaders || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        refreshHeaders: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Refresh body
                </label>
                <p className="text-xs text-slate-500">
                  Supports handlebar templating for whatever you want
                </p>
                <div className="mt-2">
                  <input
                    type="text"
                    name="refreshBody"
                    id="refreshBody"
                    value={credentialPayload.refreshBody || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        refreshBody: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </>
          )}
          {authType === "api" && (
            <>
              <div className="sm:col-span-3">
                <label
                  htmlFor="access_token"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Access Token
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="access_token"
                    id="access_token"
                    value={credentialPayload.access_token || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        access_token: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="refresh_token"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Refresh Token (Optional)
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="refresh_token"
                    id="refresh_token"
                    value={credentialPayload.refresh_token || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        refresh_token: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Endpoint (Optional)
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="endpoint"
                    id="endpoint"
                    value={credentialPayload.endpoint || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        endpoint: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </>
          )}
          {authType === "webhook" && (
            <>
              <div className="sm:col-span-2">
                <label
                  htmlFor="header"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Header
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="header"
                    id="header"
                    value={credentialPayload.header || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        header: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="key"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Key
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="key"
                    id="key"
                    value={credentialPayload.key || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        key: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="endpoint"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Endpoint
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="endpoint"
                    id="endpoint"
                    value={credentialPayload.endpoint || ""}
                    onChange={(e) =>
                      setCredentialPayload((prev) => ({
                        ...prev,
                        endpoint: e.target.value,
                      }))
                    }
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Title */}
        <div className="mt-8">
          <label
            htmlFor="title"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Note
          </label>
          <div className="mt-1 mb-8">
            <input
              id="title"
              name="title"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Credential note"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* Save and Cancel buttons */}
        <div className="flex items-center justify-end gap-x-3 pb-8 -mt-4">
          <Button
            label="Go back"
            onClick={handleGoBack}
            style="filled"
            rounded="md"
            variant="secondary"
          />
          <Button
            label="Save credential"
            onClick={handleSave}
            style="filled"
            rounded="md"
            disabled={
              createCredentialMutation.isLoading ||
              updateCredentialMutation.isLoading
            }
          />
        </div>
      </div>
    </>
  );
}
